import "./article-footer.scss";

import { SocialMediaIconProps } from "design-system/components/primitives/social-media-links/social-media-icon";
import { SocialMediaLinks } from "design-system/components/primitives/social-media-links/social-media-links";
import { SimpleTheme } from "design-system/utils/theme";
import { ReactNode } from "react";
import {
  ArticleFooterAuthorInfo,
  ArticleFooterAuthorInfoProps,
} from "./article-footer-author-info";
import {
  ArticleFooterLinkedAuthor,
  ArticleFooterLinkedAuthorProps,
} from "./article-footer-linked-author";
import { ArticleFooterTag, ArticleFooterTagProps } from "./article-footer-tag";
import {
  ContributorProps,
  Contributors,
} from "design-system/components/primitives/contributors/contributors";

export interface ArticleFooterProps {
  followUs?: Array<ArticleFooterTagProps>;
  hideAuthor?: boolean;
  authors?: Array<ArticleFooterAuthorInfoProps>;
  linkedAuthors?: Array<ArticleFooterLinkedAuthorProps>;
  contributors?: ContributorProps;
  tagsList?: Array<ArticleFooterTagProps>;
  topicsList?: Array<ArticleFooterTagProps>;
  shareList?: Array<SocialMediaIconProps>;
  theme?: SimpleTheme;
  authorHeading?: ReactNode;
}

/**
 * This component is for displaying details below article page content.
 *
 * ## See it in use on...
 * - An [article page](/story/example-pages-article-pages-big-art-topper--story)
 *
 * - **`id: CS-001-000-00`**
 * - **`data-region: cs__article-footer`**
 */
export function ArticleFooter({
  followUs,
  hideAuthor,
  authors,
  linkedAuthors,
  contributors,
  tagsList,
  topicsList,
  shareList,
  theme,
  authorHeading,
}: ArticleFooterProps) {
  return (
    <div
      className="hbs-article-footer"
      data-region="cs__article-footer"
      data-theme={theme}
    >
      {followUs && followUs.length > 0 && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">Follow Us:</h2>
          <div className="hbs-article-footer__row-content">
            {followUs.map((tag, i) => (
              <ArticleFooterTag key={i} {...tag} />
            ))}
          </div>
        </div>
      )}
      {authors && authors.length > 0 && !hideAuthor && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">
            {authorHeading ?? (authors.length > 1 ? "Authors" : "Author")}:
          </h2>
          <div className="hbs-article-footer__row-content hbs-article-footer__row-content--author">
            {authors.map((person, i) => (
              <ArticleFooterAuthorInfo {...person} key={i} />
            ))}
          </div>
        </div>
      )}

      {contributors && <Contributors {...contributors} />}

      {linkedAuthors && linkedAuthors.length > 0 && !hideAuthor && (
        <div className="hbs-article-footer__row hbs-article-footer__row--linked-author">
          <h2 className="hbs-article-footer__row-heading">
            {authorHeading ?? "Article By"}:
          </h2>
          <div className="hbs-article-footer__row-content hbs-article-footer__row-content--linked-author">
            {linkedAuthors.map((linkedAuthor, i) => (
              <ArticleFooterLinkedAuthor
                key={i}
                name={linkedAuthor.name}
                titles={linkedAuthor.titles}
                image={linkedAuthor.image}
              />
            ))}
          </div>
        </div>
      )}

      {tagsList && tagsList.length > 0 && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">Keywords:</h2>
          <div className="hbs-article-footer__row-content">
            {tagsList.map((tag: ArticleFooterTagProps, i: number) => (
              <ArticleFooterTag key={i} {...tag} />
            ))}
          </div>
        </div>
      )}

      {topicsList && topicsList.length > 0 && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">Topics:</h2>
          <div className="hbs-article-footer__row-content">
            {topicsList.map((topic: ArticleFooterTagProps, i: number) => (
              <ArticleFooterTag key={i} {...topic} />
            ))}
          </div>
        </div>
      )}

      {shareList && shareList.length > 0 && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">Share:</h2>
          <div className="hbs-article-footer__row-content hbs-article-footer_row-content--share-list">
            <SocialMediaLinks links={shareList} />
          </div>
        </div>
      )}
    </div>
  );
}
